import { CheckCircle2, HelpCircle, ThumbsDown } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { useShell } from "@/components/Shell";
import { dateRange, pct, pctDelta } from "@/lib/format";
import { cn } from "@/lib/cn";

/**
 * Health card. The "is my bot doing its job?" answer.
 *
 * Three big stats from the weekly window. Color is *support*, never the
 * sole signal — each stat carries an icon + label too. The week's
 * conversation volume sits in the eyebrow.
 */
export function HealthCard() {
  const { home, loading } = useShell();

  if (loading && !home) return <HealthCardSkeleton />;
  if (!home) return null;

  const { stats } = home;
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const range = dateRange(weekAgo.toISOString(), now.toISOString());

  const noVolume = stats.assistant_messages_this_week === 0;

  return (
    <Card>
      <div className="flex items-baseline justify-between gap-3 px-5 pt-5">
        <div>
          <div className="text-xs uppercase tracking-[0.12em] text-text-faint">This week</div>
          <div className="mt-1 flex items-baseline gap-2.5">
            <span className="text-3xl font-medium tabular-nums text-text-strong">
              {stats.conversations_this_week}
            </span>
            <span className="text-sm text-text-soft">
              {stats.conversations_this_week === 1 ? "conversation" : "conversations"}
            </span>
          </div>
        </div>
        <div className="hidden text-xs text-text-faint sm:block">{range}</div>
      </div>

      <div className="grid grid-cols-1 gap-px bg-line border-t border-line mt-5 sm:grid-cols-3">
        <Stat
          icon={CheckCircle2}
          accent="success"
          label="Answered well"
          value={noVolume ? null : stats.answered_well_pct}
          delta={pctDelta(stats.answered_well_pct, stats.answered_well_pct_prev)}
          help="Assistant replies that used retrieved context and weren't downvoted."
          empty={noVolume}
        />
        <Stat
          icon={HelpCircle}
          accent="warning"
          label='Said "I don&apos;t know"'
          value={noVolume ? null : stats.no_retrieval_pct}
          delta={pctDelta(stats.no_retrieval_pct, stats.no_retrieval_pct_prev)}
          help="Replies where retrieval returned no chunks. These appear in Content gaps."
          empty={noVolume}
        />
        <Stat
          icon={ThumbsDown}
          accent="danger"
          label="Negative feedback"
          value={noVolume ? null : stats.negative_feedback_pct}
          delta={pctDelta(stats.negative_feedback_pct, stats.negative_feedback_pct_prev)}
          help="Replies a visitor downvoted with the thumbs-down button."
          empty={noVolume}
        />
      </div>
    </Card>
  );
}

interface StatProps {
  icon: LucideIcon;
  accent: "success" | "warning" | "danger";
  label: string;
  value: number | null | undefined;
  delta: { label: string; direction: "up" | "down" | "flat" | "unknown" };
  help: string;
  empty: boolean;
}

function Stat({ icon: Icon, accent, label, value, delta, help, empty }: StatProps) {
  const accentBar = {
    success: "bg-success",
    warning: "bg-warning",
    danger:  "bg-danger",
  }[accent];
  const accentText = {
    success: "text-success",
    warning: "text-warning",
    danger:  "text-danger",
  }[accent];

  return (
    <div className="bg-surface p-5">
      <div className="flex items-center gap-1.5">
        <Icon size={14} strokeWidth={1.75} className={cn("shrink-0", accentText)} />
        <span className="text-xs text-text-soft" title={help}>{label}</span>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-[34px] font-medium leading-none tabular-nums text-text-strong">
          {empty ? "—" : pct(value)}
        </span>
      </div>
      <div className={cn("mt-3 h-1 w-12 rounded-full", accentBar)} />
      <div className="mt-3 text-xs text-text-soft">
        {empty ? "No conversations this week" : delta.label}
      </div>
    </div>
  );
}

function HealthCardSkeleton() {
  return (
    <Card>
      <div className="px-5 pt-5">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="mt-2 h-8 w-40" />
      </div>
      <div className="grid grid-cols-1 gap-px bg-line border-t border-line mt-5 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="bg-surface p-5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-3 h-9 w-20" />
            <Skeleton className="mt-3 h-1 w-12" />
            <Skeleton className="mt-3 h-3 w-32" />
          </div>
        ))}
      </div>
    </Card>
  );
}
